'use client';

/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import { useTranslation } from 'react-i18next';
import type { LearningCourseDetail } from '@akademi/site-builder-runtime';

interface StatRowProps {
  course: LearningCourseDetail;
  /** Which stats to show. Lets each variant pick its narrative. */
  show?: Array<'rating' | 'enrolled' | 'duration' | 'lessons' | 'language' | 'updated'>;
}

const DEFAULT_STATS: NonNullable<StatRowProps['show']> = [
  'rating',
  'enrolled',
  'duration',
  'lessons',
];

export function StatRow({ course, show = DEFAULT_STATS }: StatRowProps) {
  const { t } = useTranslation('site-builder-blocks');
  const items: Array<{ icon: string; label: string; value: string }> = [];

  if (show.includes('rating') && course.ratingAverage) {
    items.push({
      icon: '★',
      label: t('courseDetail.stat.rating'),
      value: `${course.ratingAverage.toFixed(1)}${
        course.ratingCount ? ` (${formatCount(course.ratingCount)})` : ''
      }`,
    });
  }
  if (show.includes('enrolled') && course.enrolled) {
    items.push({
      icon: '👥',
      label: t('courseDetail.stat.enrolled'),
      value: formatCount(course.enrolled),
    });
  }
  if (show.includes('duration') && course.duration) {
    items.push({ icon: '⏱', label: t('courseDetail.stat.duration'), value: course.duration });
  }
  if (show.includes('lessons') && course.totalLectures) {
    items.push({
      icon: '📚',
      label: t('courseDetail.stat.lessons'),
      value: String(course.totalLectures),
    });
  }
  if (show.includes('language') && course.language) {
    items.push({ icon: '🌐', label: t('courseDetail.stat.language'), value: course.language });
  }
  if (show.includes('updated') && course.lastUpdated) {
    items.push({ icon: '📅', label: t('courseDetail.stat.updated'), value: course.lastUpdated });
  }

  if (items.length === 0) return null;

  return (
    <div className="cd-statrow">
      {items.map((s, i) => (
        <span key={i} className="cd-stat">
          <span className="cd-stat-icon" aria-hidden>
            {s.icon}
          </span>
          <span className="cd-stat-value">{s.value}</span>
          <span>{s.label}</span>
        </span>
      ))}
    </div>
  );
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString('tr-TR');
}
